import { useState } from "react";
import { Phone, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TalkToExpert() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, subject: "Expert Consultation", message: `Phone: ${form.phone}\n\n${form.message}` }),
      });
      if (res.ok) {
        toast.success("We'll get back to you shortly!");
        setForm({ name: "", email: "", phone: "", message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    }
    setLoading(false);
  };

  return (
    <section className="py-20 bg-slate-900" data-testid="talk-to-expert">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Talk to an Expert</h2>
            <p className="text-lg text-slate-400 leading-relaxed">
              Get personalized guidance on compliance, carbon strategy, or project development. 
              Our team of environmental specialists is ready to help.
            </p>
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Call Us</p>
                  <p className="text-slate-400">+91 8575900009</p>
                </div>
              </div>
              <a
                href="https://wa.me/918575900009?text=Hi%2C%20I%20need%20help%20with%20environmental%20compliance%20and%20carbon%20solutions."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-xl bg-green-500/10 border border-green-500/20 hover:bg-green-500/20 transition-all group"
                data-testid="whatsapp-button"
              >
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium group-hover:text-green-300">WhatsApp Us</p>
                  <p className="text-slate-400 text-sm">Quick response guaranteed</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right - Form */}
          <form onSubmit={handleSubmit} className="bg-slate-800 rounded-2xl p-8 space-y-5" data-testid="expert-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input type="text" placeholder="Your Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none" />
              <input type="email" placeholder="Email Address" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none" />
            </div>
            <input type="tel" placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none" />
            <textarea placeholder="Tell us about your requirements..." rows={4} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none resize-none" />
            <button type="submit" disabled={loading} className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2" data-testid="expert-form-submit">
              <Send className="h-4 w-4" />
              {loading ? "Sending..." : "Get Expert Consultation"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
